import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faBuilding,
  faUser,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faGlobe,
  faSitemap,
} from "@fortawesome/free-solid-svg-icons";
import DataTable from "../../core/DataTable.jsx";
import Loading from "../../layout/Loading";
import ConfirmDelete from "../../modals/ConfirmDelete";
import Button from "../../core/Button";
import FormFieldTags from "../../input/FormFieldTags";
import SecurityWrapper from "../../auth/SecurityWrapper";
import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall";
import useDelayedLoading from "../../../hooks/useDelayedLoading";
import { useAuthInfo } from "../../../context/AuthContext";
import { formatPhone } from "../../../util/stringUtils";
import { setCachedBreadcrumbLabel } from "../../../helpers/breadcrumbCache";

const CompanyPage = (props) => {
  const { data: company, fetchData, isLoading } = useTriggeredAPICall();
  const { fetchData: updateCompanyActivation } = useTriggeredAPICall();
  const { userInfo } = useAuthInfo();
  const showLoading = useDelayedLoading(isLoading);
  const companyId = props.match.params.companyId;

  const handleActivation = async () => {
    const newActiveStatus = !company?.results.is_active;

    await updateCompanyActivation(
      `/company/${company.results.company_id}`,
      "PATCH",
      { is_active: newActiveStatus }
    );

    fetchData(`/company/${companyId}?include=contacts`);
  };

  useEffect(() => {
    if (!company) {
      fetchData(`/company/${companyId}?include=contacts`);
    }
  }, [company, companyId, fetchData]);

  useEffect(() => {
    if (company?.results?.company_id && company?.results?.name) {
      setCachedBreadcrumbLabel(
        "company",
        company.results.company_id,
        company.results.name
      );
    }
  }, [company]);

  const getCityState = (company) => {
    if (!company.city && !company.state) return "-";
    if (!company.city) return company.state;
    if (!company.state) return company.city;
    return `${company.city}, ${company.state}`;
  };

  const contactColumns = [
    {
      name: "Name",
      selector: (row) => `${row.first_name} ${row.last_name}`,
      sortable: true,
      cell: (row) => (
        <div className="contact-name">
          <FontAwesomeIcon icon={faUser} className="contact-icon" />
          {row.first_name} {row.last_name}
        </div>
      ),
    },
    {
      name: "Role",
      selector: (row) => row.role || "-",
      sortable: true,
      cell: (row) => (
        <span
          className={`role-badge role-${row.role?.toLowerCase() || "none"}`}
        >
          {row.role || "No Role"}
        </span>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.email || "-",
      sortable: true,
      cell: (row) => (
        <div className="contact-email">
          {row.email ? (
            <a href={`mailto:${row.email}`}>
              <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
              {row.email}
            </a>
          ) : (
            "-"
          )}
        </div>
      ),
    },
    {
      name: "Phone",
      selector: (row) => row.phone || "-",
      sortable: true,
      cell: (row) => (
        <div className="contact-phone">
          {row.phone ? (
            <a href={`tel:${row.phone}`}>
              <FontAwesomeIcon icon={faPhone} className="contact-icon" />
              {formatPhone(row.phone)}
            </a>
          ) : (
            "-"
          )}
        </div>
      ),
    },
  ];

  if (showLoading && !company) {
    return <Loading />;
  }

  if (!company) {
    return null;
  }

  const companyData = company.results;

  return (
    <div className="width-full company-page">
      <div className="top-section flex-space-between">
        <button onClick={() => props.history.goBack()} className="back-button">
          <FontAwesomeIcon icon={faChevronLeft} />
          Back
        </button>
        {userInfo.role?.name !== "user" && (
          <div className="flex-start active-switch-div">
            Active:
            <label className="switch company-switch">
              <input
                type="checkbox"
                onChange={handleActivation}
                checked={companyData.is_active}
              />
              <span className="slider round">
                <span>On</span>
                <span>Off</span>
              </span>
            </label>
          </div>
        )}
      </div>

      <div className="details card flex-rows flex-align-center flex-rows-space-around">
        <div className="flex-space-between">
          <h1>
            <FontAwesomeIcon icon={faBuilding} />
            {companyData.name}
          </h1>
        </div>

        <div className="middle-section pr-32">
          <div className="icon-and-details">
            <div className="height-full">
              <div className="company-info">
                {companyData.description && (
                  <p className="description">
                    <b>Description</b>: {companyData.description}
                  </p>
                )}

                <div className="contact-details">
                  <p className="organization">
                    <FontAwesomeIcon icon={faSitemap} />
                    <b>Organization</b>: {companyData.organization?.name || "—"}
                  </p>

                  {companyData.website && (
                    <p className="website">
                      <FontAwesomeIcon icon={faGlobe} />
                      <b>Website</b>:{" "}
                      <a
                        href={companyData.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {companyData.website}
                      </a>
                    </p>
                  )}

                  {companyData.phone && (
                    <p className="phone">
                      <FontAwesomeIcon icon={faPhone} />
                      <b>Phone</b>:{" "}
                      <a href={`tel:${companyData.phone}`}>
                        {formatPhone(companyData.phone)}
                      </a>
                    </p>
                  )}

                  {companyData.email && (
                    <p className="email">
                      <FontAwesomeIcon icon={faEnvelope} />
                      <b>Email</b>:{" "}
                      <a href={`mailto:${companyData.email}`}>
                        {companyData.email}
                      </a>
                    </p>
                  )}

                  {(companyData.address ||
                    companyData.city ||
                    companyData.state ||
                    companyData.zip_code) && (
                    <p className="address">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      <b>Address</b>:{" "}
                      {[
                        companyData.address,
                        getCityState(companyData),
                        companyData.zip_code,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex-rows flex-align-start button-row-right">
            {userInfo.role?.name !== "user" && (
              <>
                <Button
                  onClick={() =>
                    props.history.push(
                      `/company-form/${companyData.company_id}`
                    )
                  }
                >
                  Edit Company
                </Button>
              </>
            )}

            <SecurityWrapper
              requiredPermissions={["delete_companies_my_organization"]}
            >
              <ConfirmDelete objectType="company" id={companyData.company_id} />
            </SecurityWrapper>
          </div>
        </div>
      </div>

      <div className="contacts-section">
        <div className="section-header flex-rows flex-space-between">
          <h2>
            <FontAwesomeIcon icon={faUser} />
            Contacts ({companyData.contacts?.length || 0})
          </h2>

          {userInfo.role?.name !== "user" && (
            <Button
              onClick={() =>
                props.history.push(
                  `/contact-add?company_id=${companyData.company_id}`
                )
              }
            >
              + Add Contact
            </Button>
          )}
        </div>

        {companyData.contacts && companyData.contacts.length > 0 ? (
          <DataTable
            columns={contactColumns}
            data={companyData.contacts}
            pointerOnHover
            highlightOnHover
          />
        ) : (
          <div className="no-contacts">
            <p>No contacts found for this company.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyPage;
